import CustomModal from '../../../common/components/modal/CustomModal';
import React from 'react';
import {FormikProps, useFormik} from 'formik';
import {PaymentAddress, PaymentAddressFormValues} from '../../../interfaces/payment-address';
import PaymentAddressForm from './PaymentAddressForm';
import {useIntl} from 'react-intl';
import * as Yup from 'yup';
import {QueryObserverResult, RefetchOptions, RefetchQueryFilters, useMutation} from 'react-query';
import toast from 'react-hot-toast';
import {editPaymentAddress} from '../api';
import ButtonReset from '../../../modules/form/ButtonReset';
import ButtonSubmit from '../../../modules/form/ButtonSubmit';
import {Entity} from '../../../modules/auth/authorization/api';
import {useGetMe} from "../../../hooks/useGetMe";
import {CustomerSystem} from "../../../../Domains/Auth/Types/customer";

interface PaymentAddressEditModalProps {
    initialPaymentAdress: PaymentAddress;
    show: boolean;
    hide: () => void;
    entity: Entity;
    refetch: <TPageData>(
        options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
    ) => Promise<QueryObserverResult<PaymentAddress>>;
}

const PaymentAddressEditModal = ({
    initialPaymentAdress,
    hide,
    show,
    refetch,
}: PaymentAddressEditModalProps): JSX.Element => {
    const intl = useIntl();
    const customer = useGetMe();

    const {mutateAsync, isLoading} = useMutation({
        mutationFn: async (paymentAddress: PaymentAddress) => {
            return await editPaymentAddress(paymentAddress, customer.id.toString());
        },
        onSuccess: async () => {
            await refetch();
            toast.success(
                intl.formatMessage({id: 'ADMINISTRATIVE.PAYMENT_MODE.RIB.BANK.FORM.SUCCESS'})
            );
            hide();
        },
        onError: () => {
            toast.error(
                intl.formatMessage({id: 'ADMINISTRATIVE.PAYMENT_MODE.RIB.BANK.FORM.ERROR'})
            );
        },
    });

    const validationRules: Yup.SchemaOf<PaymentAddressFormValues> = Yup.object().shape({
        address: Yup.string().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        zipCode: Yup.string()
            .required(intl.formatMessage({id: 'FIELD.REQUIRED'}))
            .max(customer.system === CustomerSystem.CIS ? 30 : 5),
        city: Yup.string().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
        country: Yup.string().required(intl.formatMessage({id: 'FIELD.REQUIRED'})),
    });

    const paymentAdressForm: FormikProps<PaymentAddress> = useFormik({
        initialValues: initialPaymentAdress,
        validationSchema: validationRules,
        validateOnChange: true,
        enableReinitialize: true,
        onSubmit: async (values: PaymentAddress, {resetForm}) => {
            await mutateAsync(values);
            resetForm();
        },
    });

    const reset = () => {
        hide();
        paymentAdressForm.resetForm();
    };

    return (
        <CustomModal
            title={intl.formatMessage({id: 'ADMINISTRATIVE.PAYMENT_ADDRESS_EDIT_MODAL.TITLE'})}
            show={show}
            onHide={reset}
            body={<PaymentAddressForm system={customer.system} formik={paymentAdressForm} />}
            footer={
                <>
                    <ButtonReset
                        onClick={() => {
                            reset();
                        }}
                        textId={'CANCEL'}
                    />
                    <ButtonSubmit
                        type={'button'}
                        disabled={!paymentAdressForm.isValid || paymentAdressForm.isSubmitting}
                        isLoading={isLoading || paymentAdressForm.isSubmitting}
                        textId={'ADMINISTRATIVE.PAYMENT_ADDRESS_EDIT_MODAL.SUBMIT'}
                        onClick={() => {
                            paymentAdressForm.submitForm();
                        }}
                    />
                </>
            }
        />
    );
};

export default PaymentAddressEditModal;
